<template lang="pug">
  .wrap
    .header-wrap
      page-title.report-title(fontSize='30px') Your courses report
      .button-wrap
        a(:href="reportCsvUrl")
          el-button Export as CSV

    .user-filters
      el-form(@submit.native.prevent='search')
        el-row(:gutter='20')
          el-col(:sm='8')
            .filter-label Groups
            el-select(v-model="searchParams.groups" multiple placeholder="All")
              el-option(v-for='licenseGroup in searchOptions.groups'
                      :key='licenseGroup.id'
                      :label='licenseGroup.group.name'
                      :value='licenseGroup.id') {{ licenseGroup.group.name }}


          el-col(:sm='12')
            .filter-label Course view
            el-select(v-model="searchParams.courses" multiple placeholder="All")
              el-option(v-for="entry in courseOptions" :key="entry.key" :label="entry.title" :value="entry.id")
                i.el-icon-minus.course-option-icon(v-if='entry.type === "course"')
                strong(v-if="entry.type === 'program'") {{ entry.title }}
                span(v-else) {{ entry.title }}

          el-col(:sm='4')
            el-button.submit-filters(native-type="submit") Filter
            el-button.submit-filters(@click="resetFilters") Reset

    .el-table.el-table--fit.el-table--border(v-loading='reports.users.loading' :style={marginTop: '20px'})
      .el-table__body-wrapper.table-scroller
        table.el-table__body(cellpadding='0' cellspacing='0' border='0')
          tbody
            tr.el-table__row
              th.fixed-cell(:style='{minWidth: "100px", maxWidth: "200px"}')
                .cell User

              th.is-center.stat-cell(v-for='course in courseHeader' :key='course.id' :style='{minWidth: "100px", maxWidth: "200px"}')
                .cell {{ course.title }}

          tr.el-table__row(v-for='user in usersData' :key='user.id')
            td.fixed-cell
              .cell.user-cell
                .name {{ user.firstName }} {{ user.lastName }}
                .email {{ user.email }}

            td.is-center.fixed-cell(v-for='pivotEntry in user.pivotedCourses' :key='pivotEntry.course.id')
              .cell
                template(v-if='pivotEntry.assigned')
                  template(v-if='pivotEntry.passedAttempt')
                    el-popover(content="Bottom center" placement="top")
                      icon.status-icon(slot="reference" name='check-circle' color='green' :scale='1.3')
                      div
                        p Time: {{ pivotEntry.passedAttempt.sessionTime }}
                        p Score: {{ pivotEntry.passedAttempt.score }}%
                        p Attempts: {{ pivotEntry.results.length }}
                  template(v-else)
                    template(v-if='pivotEntry.inProgressAttempt')
                      el-popover(content="Bottom center" placement="top")
                        icon.status-icon(slot="reference" name='clock' color='orange' :scale='1.3')
                        div
                          p Time: {{ pivotEntry.inProgressAttempt.sessionTime }}
                          p Score: {{ pivotEntry.inProgressAttempt.score }}%
                    template(v-else-if='pivotEntry.failedAttempt')
                      el-popover(content="Bottom center" placement="top")
                        icon.status-icon(slot="reference" name='times-circle' color='darkred' :scale='1.3')
                        div
                          p Time: {{ pivotEntry.failedAttempt.sessionTime }}
                          p Score: {{ pivotEntry.failedAttempt.score }}%

                template(v-else)
                  icon.status-icon(name='minus' color='#eee' :scale='1.3')

</template>

<script>
import _ from "lodash";
import { mapActions } from "vuex";
import format from "date-fns/format";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import ChartWrap from "@/shared/components/ChartWrap";
import CrudMixin from "@/shared/mixins/Crud";

import PageTitle from "@/shared/components/layout/PageTitle";
import TimeSwitcher from "@/brapp/components/shared/TimeSwitcher";
import CircularCounter from "@/shared/components/CircularCounter";

const INITIAL_SEARCH_PARAMS = {
  q: "",
  groups: [],
  courses: [],
  status: []
};

const formatApiDate = date => format(date, "YYYY-MM-DD");

export default {
  components: {
    PageTitle,
    TimeSwitcher,
    CircularCounter,
    ChartWrap
  },
  mixins: [CrudMixin],
  data() {
    const now = Date.now();
    const dateRange = [
      formatApiDate(startOfWeek(now, { weekStartsOn: 1 })),
      formatApiDate(endOfWeek(now, { weekStartsOn: 1 }))
    ];
    return {
      program: null,
      mode: "weekly",
      searchOptions: {
        courses: [],
        groups: [],
        status: [
          { id: "passed", name: "Passed" },
          { id: "failed", name: "Failed" },
          { id: "incomplete", name: "Incomplete" }
        ]
      },
      searchParams: _.cloneDeep(INITIAL_SEARCH_PARAMS),
      reports: {
        users: {
          loading: false,
          data: []
        }
      },
      dateRange
    };
  },
  computed: {
    reportCsvUrl() {
      const params = {
        format: "csv",
        ...this.normalisedSearchParams,
        company: this.$store.getters["auth/activeCompany"].id
      };
      return this.$store.getters["api/generateUrl"]({
        path: "/itrain/reports/courses/",
        params
      });
    },
    normalisedSearchParams() {
      return {
        ..._.omit(this.searchParams, "courses"),
        course_ids: this.searchParams.courses
          .filter(entry => entry.startsWith("course"))
          .map(entry => entry.replace("course-", "")),
        program_ids: this.searchParams.courses
          .filter(entry => entry.startsWith("program"))
          .map(entry => entry.replace("program-", ""))
      };
    },
    courseHeader() {
      return this.reports.users.data;
    },
    statusOptions() {
      return [
        { value: "", label: "All" },
        { value: "pass", label: "Pass" },
        { value: "fail", label: "Fail" },
        { value: "in_progress", label: "In progress" }
      ].map(opt => ({
        ...opt,
        disabled: opt.value && this.searchParams.status.indexOf("") > -1
      }));
    },
    courseOptions() {
      const options = [];
      this.searchOptions.courses.forEach(program => {
        options.push({
          key: `program-${program.id}`,
          id: `program-${program.id}`,
          type: "program",
          title: program.title
        });

        program.programCourses.forEach(programCourse => {
          options.push({
            key: `course-${programCourse.id}`,
            id: `course-${programCourse.course.id}`,
            type: "course",
            title: programCourse.course.title
          });
        });
      });

      return options;
    },
    usersData() {
      const { data } = this.reports.users;

      const pivotedData = _.sortBy(
        _.uniqBy(
          _.flatMap(data.map(course => course.users)).map(user => {
            return {
              ...user,
              pivotedCourses: data.map(topLevelCourse => {
                const assignedCourse = data.find(
                  course =>
                    course.id === topLevelCourse.id &&
                    course.users.find(courseUser => courseUser.id === user.id)
                );

                const userResult =
                  assignedCourse &&
                  assignedCourse.users.find(
                    courseUser => courseUser.id === user.id
                  );

                return {
                  course: topLevelCourse,
                  assigned: !!assignedCourse,
                  results: userResult ? userResult.results : null,
                  passedAttempt: _.get(userResult, ["passedAttempt", 0], null),
                  failedAttempt: _.get(userResult, ["failedAttempt", 0], null),
                  inProgressAttempt: _.get(
                    userResult,
                    ["inProgressAttempt", 0],
                    null
                  )
                };
              })
            };
          }),
          user => user.id
        ),
        ["firstName", "lastName"]
      );

      return pivotedData;
    },
    hasSearchParams() {
      return _.some(_.values(this.normalisedSearchParams));
    }
  },
  // watch: {
  //   searchParams: {
  //     handler() {
  //       this.loadReports();
  //     },
  //     deep: true
  //   }
  // },
  methods: {
    ...mapActions({
      fetchGroups: "appLicenseGroups/fetch"
    }),
    loadReports() {
      this.loadUsersReport();
    },
    search() {
      this.loadUsersReport();
    },
    async loadUsersReport() {
      this.reports.users.loading = true;

      const params = {
        ...this.normalisedSearchParams,
        company: this.$store.getters["auth/activeCompany"].id
      };

      const data = await this.$store.dispatch("api/sendRequest", {
        method: "get",
        url: `/itrain/reports/courses/`,
        params
      });

      this.reports.users.data = data;
      this.reports.users.loading = false;
    },
    async loadUserGroups() {
      this.searchOptions.groups = await this.fetchGroups({
        params: { app_license__app__codename: "itrain" }
      });
    },
    async loadCourses() {
      this.searchOptions.courses = await this.$store.dispatch(
        "api/sendRequest",
        {
          method: "get",
          url: "/itrain/programs/course_options",
          params: { company: this.$store.getters["auth/activeCompany"].id }
        }
      );
    },
    resetFilters() {
      this.searchParams = _.cloneDeep(INITIAL_SEARCH_PARAMS);
      this.search();
    }
  },
  created() {
    this.loadReports();
    this.loadUserGroups();
    this.loadCourses();
  }
};
</script>

<style>
.subheading {
  font-size: 13px;
  margin-bottom: 10px;
}
</style>
<style scoped lang="scss">
@import "~@/assets/scss/_mixins.scss";

.header-wrap {
  display: flex;
  justify-content: space-between;
}

.course-report {
  background: white;
  padding: 20px;
}

.checkbox-wrap {
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;

  .el-checkbox {
    margin: 0 0 5px 0;
    min-width: 200px;
  }
}
.checkbox-wrap-long {
  margin: 20px 0;
  .group-title {
    margin-right: 20px;
  }
}

.button-wrap {
  float: right;
}

.table-scroller {
  overflow-x: auto;
}

.fixed-cell {
  min-width: 200px;
  max-width: 400px;
}

.status-icon {
  cursor: pointer;
}

.user-cell {
  padding: 15px;

  .email {
    font-size: 13px;
    color: #999;
    margin-top: -8px;
  }
}

.name {
  font-size: 15px;
  margin-bottom: 5px;
}

.email {
  font-size: 13px;
}

.overall-chart-wrap {
  background: white;
  flex: 1;
}

.empty-results {
  padding: 50px 0;
  text-align: center;
}

.results-info {
  margin: 10px 0;
}

.user-filters {
  background: white;
  padding: 20px;
  margin-bottom: 10px;
}

.submit-filters {
  margin-top: 25px;
}

.filter-label {
  font-size: 14px;
  margin-bottom: 8px;
}

.course-option-icon {
  margin-right: 5px;
  color: #ccc;
}
</style>
